/**
 * Provider de autenticação Keycloak agnóstico
 * Encapsula keycloak-js e adapta para a interface AuthContextState
 */
import React, { useEffect, useState, useMemo, useCallback, ReactNode, useRef } from 'react';
import Keycloak from 'keycloak-js';
import { AuthContext } from '../context';
import { UserProfile, AuthContextState } from '../types';
import logger from '@/shared/lib/logger';
import { setCurrentToken } from '../utils/tokenManager';
import {
  registerTokenRefresh,
  clearTokenRefresh,
  registerTokenRefreshFailureHandler,
  clearTokenRefreshFailureHandler,
} from '../utils/tokenRefresh';

interface KeycloakAuthProviderProps {
  children: ReactNode;
}

const DISABLE_AUTH = import.meta.env.VITE_DISABLE_AUTH === 'true';

/** Verifica se o Keycloak está configurado */
const isKeycloakConfigured = (): boolean => {
  const url = import.meta.env.VITE_KEYCLOAK_URL;
  const realm = import.meta.env.VITE_KEYCLOAK_REALM;
  const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID;
  return !!(url && realm && clientId);
};

/** Obtém configuração do Keycloak das variáveis de ambiente */
const getKeycloakConfig = () => {
  return {
    url: import.meta.env.VITE_KEYCLOAK_URL || '',
    realm: import.meta.env.VITE_KEYCLOAK_REALM || '',
    clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || ''
  };
};

/** Mapeia token Keycloak para UserProfile genérico */
interface KeycloakTokenLike {
  sub?: string;
  email?: string;
  preferred_username?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  picture?: string;
  realm_access?: {
    roles?: string[];
  };
}

const mapKeycloakUser = (tokenParsed: KeycloakTokenLike): UserProfile => {
  return {
    id: tokenParsed?.sub || '',
    email: tokenParsed?.email || tokenParsed?.preferred_username || '',
    name: tokenParsed?.name || tokenParsed?.preferred_username || 'Unknown',
    roles: tokenParsed?.realm_access?.roles || [],
    avatarUrl: tokenParsed?.picture,
    sub: tokenParsed?.sub,
    preferred_username: tokenParsed?.preferred_username,
    given_name: tokenParsed?.given_name,
    family_name: tokenParsed?.family_name,
  };
};
/** Componente Provider de autenticação Keycloak */
export const KeycloakAuthProvider: React.FC<KeycloakAuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  /** Ref para manter token sincronizado (evita race conditions) */
  const tokenRef = useRef<string | null>(null);
  /** Previne dupla inicialização (React StrictMode) */
  const initializationRef = useRef<boolean>(false);
  /** Controla montagem para evitar update de estado após unmount */
  const mountedRef = useRef<boolean>(true);

  /** Instância Keycloak (singleton) */
  const keycloak = useMemo(() => {
    if (DISABLE_AUTH || !isKeycloakConfigured()) {
      return new Keycloak({
        url: 'http://localhost',
        realm: 'dummy',
        clientId: 'dummy'
      });
    }
    return new Keycloak(getKeycloakConfig());
  }, []);
  /**
   * Zera o estado local de sessão (token + user + isAuthenticated).
   * Reativo: ProtectedRoute observa isAuthenticated e redireciona sozinho,
   * mesmo que a rota já esteja montada há muito tempo (sem precisar de um clique).
   */
  const clearSessionState = useCallback(() => {
    tokenRef.current = null;
    setCurrentToken(null);
    setToken(null);
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  const updateToken = useCallback(async (minValidity: number = 30): Promise<string | null> => {
    if (DISABLE_AUTH || !isKeycloakConfigured()) {
      const dummyToken = 'dummy-token';
      tokenRef.current = dummyToken;
      setCurrentToken(dummyToken);
      return dummyToken;
    }

    if (!keycloak.authenticated || !keycloak.tokenParsed) {
      return null;
    }

    const expiresIn = keycloak.tokenParsed.exp! - Math.ceil(Date.now() / 1000);

    if (expiresIn > minValidity) {
      // Token still valid, return current token
      return keycloak.token || null;
    }

    try {
      const refreshed = await keycloak.updateToken(minValidity);
      if (refreshed && keycloak.token) {
        logger.debug('[KeycloakAuthProvider] Token renovado');
        
        const newToken = keycloak.token;
        
        tokenRef.current = newToken;
        setCurrentToken(newToken);
        setToken(newToken);
        
        return newToken;
      }
      return keycloak.token || null;
    } catch (error) {
      logger.error('[KeycloakAuthProvider] Falha ao renovar token:', error);
      return null;
    }
  }, [keycloak]);
  const updateAuthState = useCallback(async (skipTokenUpdate = false) => {
    logger.debug('[KeycloakAuthProvider] Updating auth state', { skipTokenUpdate });

    try {
      let currentTokenValue = tokenRef.current;
      
      /** Tenta renovar token se não for para pular */
      if (!skipTokenUpdate) {
        try {
          const newToken = await updateToken(60);
          if (newToken) {
            currentTokenValue = newToken;
          }
        } catch (error) {
          logger.warn('[KeycloakAuthProvider] Token não pôde ser renovado', { error });
        }
      }

      const isAuth = keycloak.authenticated || false;
      
      if (isAuth && keycloak.tokenParsed) {
        const userProfile = mapKeycloakUser(keycloak.tokenParsed);
        const userToken = keycloak.token || null;

        logger.info('[KeycloakAuthProvider] Usuário autenticado', {
          email: userProfile.email,
          name: userProfile.name,
          hasToken: !!userToken,
          tokenLength: userToken?.length || 0,
        });

        setIsAuthenticated(true);
        setUser(userProfile);
        
        /** Garante que token está sincronizado em todos os lugares */
        if (userToken) {
          if (tokenRef.current !== userToken) {
            logger.debug('[KeycloakAuthProvider] Atualizando token', {
              tokenLength: userToken.length,
            });
            tokenRef.current = userToken;
            setCurrentToken(userToken);
            setToken(userToken);
          } else if (skipTokenUpdate) {
            logger.debug('[KeycloakAuthProvider] Sincronizando tokenManager');
            setCurrentToken(userToken);
          }
        }
      } else {
        logger.info('[KeycloakAuthProvider] Usuário não autenticado');
        clearSessionState();
      }
    } catch (error) {
      logger.error('[KeycloakAuthProvider] Falha ao atualizar estado de auth', { error });
    }
  }, [keycloak, updateToken, clearSessionState]);
  const login = useCallback(() => {
    if (DISABLE_AUTH || !isKeycloakConfigured()) {
      logger.debug('[KeycloakAuthProvider] Auth desabilitada, simulando login');
      window.location.href = window.location.origin;
      return;
    }

    keycloak.login({
      redirectUri: window.location.origin + '/callback',
    });
  }, [keycloak]);
  const logout = useCallback(() => {
    if (DISABLE_AUTH || !isKeycloakConfigured()) {
      logger.debug('[KeycloakAuthProvider] Auth desabilitada, simulando logout');
      clearTokenRefresh();
      clearTokenRefreshFailureHandler();
      window.location.href = window.location.origin;
      return;
    }

    keycloak.logout({
      redirectUri: window.location.origin,
    });

    clearSessionState();
    clearTokenRefresh();
    clearTokenRefreshFailureHandler();
  }, [keycloak, clearSessionState]);
  const hasRole = useCallback((role: string): boolean => {
    return user?.roles.includes(role) || false;
  }, [user]);

  /** Inicializa o Keycloak e configura o provider (executa apenas uma vez) */
  useEffect(() => {
    mountedRef.current = true;

    if (initializationRef.current) {
      logger.debug('[KeycloakAuthProvider] Já inicializado, pulando');
      return;
    }

    const initAuth = async () => {
      logger.info('[KeycloakAuthProvider] Initializing Keycloak');

      if (DISABLE_AUTH) {
        logger.warn('[KeycloakAuthProvider] Auth disabled via VITE_DISABLE_AUTH');
        const dummyToken = 'dummy-token';
        
        tokenRef.current = dummyToken;
        setCurrentToken(dummyToken);
        setToken(dummyToken);
        
        setIsAuthenticated(true);
        setUser({
          id: 'dummy_id',
          email: 'dummy@example.com',
          name: 'Usuário Dummy',
          roles: [],
        });
        setIsLoading(false);
        initializationRef.current = true;
        return;
      }

      if (!isKeycloakConfigured()) {
        logger.warn('[KeycloakAuthProvider] Keycloak not configured');
        setIsLoading(false);
        initializationRef.current = true;
        return;
      }

      try {
        /** Detecta se é callback do SSO */
        const isCallback = window.location.href.includes('state=') && window.location.href.includes('code=');
        
        const initOptions = isCallback
          ? {
              checkLoginIframe: false,
              pkceMethod: 'S256' as const,
              enableLogging: import.meta.env.DEV,
            }
          : {
              onLoad: 'check-sso' as const,
              checkLoginIframe: false,
              pkceMethod: 'S256' as const,
              enableLogging: import.meta.env.DEV,
            };

        const authenticated = await keycloak.init(initOptions);
        logger.info('[KeycloakAuthProvider] Keycloak inicializado', { authenticated });

        /** Se autenticado, obtém token imediatamente */
        if (authenticated && keycloak.token) {
          logger.info('[KeycloakAuthProvider] Token disponível após init', {
            tokenLength: keycloak.token.length,
          });
          
          const newToken = keycloak.token;
          tokenRef.current = newToken;
          setCurrentToken(newToken);
          setToken(newToken);
        }
        keycloak.onAuthSuccess = () => {
          logger.info('[KeycloakAuthProvider] Auth success');
          if (keycloak.token) {
            tokenRef.current = keycloak.token;
            setCurrentToken(keycloak.token);
            setToken(keycloak.token);
          }
          updateAuthState(true);
        };

        keycloak.onAuthLogout = () => {
          logger.info('[KeycloakAuthProvider] Logout');
          clearSessionState();
        };

        /**
         * Disparado pelo keycloak-js sempre que uma renovação falha — seja originada
         * pelo timer de 5 min, por onTokenExpired, ou por qualquer chamada manual a
         * updateToken (ex.: o refresh proativo do httpClient antes de uma requisição).
         * O próprio keycloak-js só zera kc.authenticated quando o servidor responde
         * 400 (refresh token realmente inválido/expirado — invalid_grant); em erro de
         * rede transitório, kc.authenticated permanece true e não devemos deslogar.
         * Sem checar isso, isAuthenticated do React ficava "preso" em true enquanto a
         * página já estava montada, e o app só descobria a sessão morta quando o
         * usuário clicava em algo e a requisição batia um 401.
         */
        keycloak.onAuthRefreshError = () => {
          if (!keycloak.authenticated) {
            logger.error('[KeycloakAuthProvider] Falha ao renovar sessão (refresh token inválido)');
            clearSessionState();
          } else {
            logger.warn('[KeycloakAuthProvider] Falha ao renovar token, provável erro de rede — sessão local mantida');
          }
        };

        keycloak.onTokenExpired = () => {
          logger.info('[KeycloakAuthProvider] Token expirado');
          updateToken(5).catch((error) => {
            logger.error('[KeycloakAuthProvider] Falha ao renovar token expirado', { error });
          });
        };

        await updateAuthState(true);
        
        /** Registra função de refresh para uso pelo httpClient */
        registerTokenRefresh(updateToken);
        registerTokenRefreshFailureHandler(async (reason) => {
          logger.warn('[KeycloakAuthProvider] Falha definitiva no refresh, iniciando login', { reason });
          login();
        });
        logger.info('[KeycloakAuthProvider] Função de refresh registrada');
        
        initializationRef.current = true;
      } catch (error) {
        logger.error('[KeycloakAuthProvider] Falha ao inicializar', { error });
        clearSessionState();
        initializationRef.current = true;
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    initAuth();

    return () => {
      mountedRef.current = false;
      clearTokenRefreshFailureHandler();
    };
  }, [keycloak, login, updateAuthState, updateToken, clearSessionState]);

  /** Verifica estado de auth periodicamente após inicialização */
  useEffect(() => {
    if (isLoading || !initializationRef.current) {
      return;
    }

    const authCheckInterval = setInterval(() => {
      updateAuthState();
    }, 300000);

    return () => {
      clearInterval(authCheckInterval);
    };
  }, [isLoading, updateAuthState]);
  const value = useMemo<AuthContextState>(() => ({
    isAuthenticated,
    isLoading,
    user,
    token,
    login,
    logout,
    hasRole,
  }), [isAuthenticated, isLoading, user, token, login, logout, hasRole]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
